import { useMemo, useRef } from 'react';

import { useMount } from 'react-use';

import { Button, Stack, Typography } from '@mui/material';

import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';

import ExternalLinkIcon from '@/components/Icons/ExternalLinkIcon';
import ToolIcon from '@/components/Icons/ToolIcon';
import useIsTrainingEnabled from '@/hooks/useIsTrainingEnabled';
import { links } from '@/localization';
import { WorkspaceTrainingProjectFragment } from '@/sections/Overview/Training/__generated__/fragments.generated';
import { useTrainingProjects } from '@/sections/Overview/Training/hooks';
import ListOptions from '@/sections/Workspace/Filter/ListOptions';
import Search from '@/sections/Workspace/Filter/Search';
import ViewModes from '@/sections/Workspace/Filter/ViewModes';
import { ViewMode } from '@/sections/Workspace/types';
import Fuse from 'fuse.js';
import moment from 'moment';

import { BASE_FUSE_SEARCH_OPTIONS, mapSortByTrainingToTitle } from '../constants';
import { useFilterQuery, useFilterSortBy, useTableContainerHeight, useViewMode } from '../hooks';
import EmptyState, { Loading } from '../sections/EmptyStates';
import { IconContainer } from '../styled';
import { SortByTraining, WorkspaceSections } from '../types';
import TrainingEmptyState from './EmptyState';
import Grid from './Grid';
import Table from './Table';
import { TrainingProps } from './types';

const FUSE_SEARCH_OPTIONS = {
  ...BASE_FUSE_SEARCH_OPTIONS,
  keys: ['name', 'id'],
};

function Training({ minimal = false }: TrainingProps) {
  const { trainingProjects, loading, refetch } = useTrainingProjects(true);
  const { viewMode, setViewMode } = useViewMode(WorkspaceSections.Training, minimal);
  const { filterQuery, setFilterQuery } = useFilterQuery(WorkspaceSections.Training);
  const { filterSortBy, setFilterSortBy } = useFilterSortBy(WorkspaceSections.Training);
  const isTrainingEnabled = useIsTrainingEnabled();
  const containerRef = useRef<HTMLDivElement>(null);
  const tableContainerHeight = useTableContainerHeight(containerRef, Number(loading));

  useMount(() => {
    refetch();
  });

  const sortedTrainingProjects = useMemo(() => {
    if (filterSortBy === SortByTraining.RECENTLY_ACTIVE) {
      return sortBy(trainingProjects, (trainingProject) =>
        moment(trainingProject.lastActivity).valueOf(),
      ).reverse();
    }

    return sortBy(trainingProjects, (trainingProject) =>
      moment(trainingProject.created).valueOf(),
    ).reverse();
  }, [trainingProjects, filterSortBy]);

  const filteredTrainingProjects = useMemo(() => {
    if (!filterQuery) {
      return sortedTrainingProjects;
    }

    const fuse = new Fuse<WorkspaceTrainingProjectFragment>(
      sortedTrainingProjects,
      FUSE_SEARCH_OPTIONS,
    );

    return fuse.search(filterQuery).map(({ item }) => item);
  }, [filterQuery, sortedTrainingProjects]);

  function getContent() {
    if (loading) {
      return <Loading />;
    }

    if (isEmpty(trainingProjects)) {
      return <TrainingEmptyState />;
    }

    if (isEmpty(filteredTrainingProjects)) {
      return <EmptyState>No Training Projects found</EmptyState>;
    }

    return viewMode === ViewMode.Grid ? (
      <Grid trainingProjects={filteredTrainingProjects} />
    ) : (
      <Table
        trainingProjects={filteredTrainingProjects}
        height={tableContainerHeight}
        minimal={minimal}
      />
    );
  }

  return (
    <>
      <Stack height="100%">
        {!minimal && (
          <Stack gap={3} px={4} pt={4} pb={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" gap={2}>
                <IconContainer>
                  <ToolIcon />
                </IconContainer>
                <Typography variant="h2">Training</Typography>
              </Stack>
              {isTrainingEnabled && (
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={<ExternalLinkIcon />}
                  href={links.basetenDocsTrainingOverview}
                  target="_blank"
                >
                  Start a Training project
                </Button>
              )}
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Search query={filterQuery} setQuery={setFilterQuery} />
              <ListOptions<SortByTraining>
                title="Sort"
                options={Object.values(SortByTraining)}
                selectedOption={filterSortBy as SortByTraining}
                onOptionSelect={setFilterSortBy}
                mapOptionToTitle={(option) => mapSortByTrainingToTitle[option]}
              />
              <ViewModes viewMode={viewMode} setViewMode={setViewMode} />
            </Stack>
          </Stack>
        )}
        <Stack flex={1} ref={containerRef} overflow="hidden">
          {getContent()}
        </Stack>
      </Stack>
    </>
  );
}

export default Training;
